/**
 * Phase 8 · C-49 · Shared viz — MiniSparkline.
 *
 * Pure SVG polyline, no axes, no chart lib. Scales to the container width
 * via viewBox preserveAspectRatio.
 */
import './tokens.css';

export type MiniSparklineTone = 'neutral' | 'success' | 'warning' | 'danger';

export interface MiniSparklineProps {
  data: number[];
  height?: number;
  tone?: MiniSparklineTone;
  /** Optional accessible label (defaults to a terse stats summary). */
  ariaLabel?: string;
}

const TONE_VAR: Record<MiniSparklineTone, string> = {
  neutral: 'var(--viz-neutral)',
  success: 'var(--viz-success)',
  warning: 'var(--viz-warning)',
  danger:  'var(--viz-danger)',
};

export function MiniSparkline({ data, height = 32, tone = 'neutral', ariaLabel }: MiniSparklineProps) {
  const W = 100;
  const H = height;
  const color = TONE_VAR[tone];

  if (data.length === 0) {
    return (
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={ariaLabel ?? 'No data'}>
        <line x1={0} y1={H / 2} x2={W} y2={H / 2} stroke="var(--viz-border)" strokeWidth={1} strokeDasharray="2 3" />
      </svg>
    );
  }

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = data.length > 1 ? W / (data.length - 1) : 0;

  const points = data
    .map((v, i) => {
      const x = i * step;
      const y = H - ((v - min) / range) * (H - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');

  const areaPoints = `0,${H} ${points} ${W},${H}`;
  const latest = data[data.length - 1]!;
  const prev = data[0]!;
  const label = ariaLabel ?? `Sparkline ${data.length} points, latest ${latest}, first ${prev}`;

  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <polygon points={areaPoints} fill={color} opacity={0.12} />
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
